import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, generatePath } from 'react-router-dom';

import {
  fsmCfg,
  fsmConfigPatchForFR,
  fsmConfigPatchForUK,
  fsmConfigPatchForUS,
} from '@/modules/onboarding/fsm-config';
import type { TFsmEvent, TFsmState } from '@/modules/onboarding/fsm-types';
import { fsmSelectors, fsmAsyncActions, fsmSlice } from '@/modules/onboarding/store/fsm-slice';
import { AppDispatch } from '@/root-store';
import { TCountryCodes } from '@/shared/models/country-code';

import { WizardView } from './view';

interface IProps {
  initialValue: TFsmState;
  partnerFirstName: string;
  partnerCountryCode?: TCountryCodes;
}

const WizardManager: React.FC<IProps> = ({
  initialValue,
  partnerFirstName,
  partnerCountryCode,
}) => {
  const history = useHistory();
  const dispatch = useDispatch() as AppDispatch;

  useEffect(() => {
    const onUpdateFsmValue = (newValue: string, context?: Record<string, string>) => {
      const internalPath = generatePath(newValue, context);

      history.replace(internalPath + (context?.hash ? `#${context.hash}` : ''));
    };

    dispatch(fsmSlice.actions.setOnUpdate(onUpdateFsmValue));
  }, [dispatch, history]);

  // The entry screen is resolved once, when the wizard opens. Each step stores
  // the server's onboarding progress and then sends NEXT itself; following
  // `initialValue` after that moves the wizard first and NEXT skips a screen.
  const initialValueRef = useRef(initialValue);

  useEffect(() => {
    dispatch(fsmAsyncActions.trySetValue({ newValue: initialValueRef.current }));
  }, [dispatch]);

  useEffect(() => {
    if (partnerCountryCode === 'GB') {
      dispatch(fsmSlice.actions.patchTransitions(fsmConfigPatchForUK));

      return;
    }

    if (partnerCountryCode === 'FR') {
      dispatch(fsmSlice.actions.patchTransitions(fsmConfigPatchForFR));

      return;
    }

    if (partnerCountryCode === 'US') {
      dispatch(fsmSlice.actions.patchTransitions(fsmConfigPatchForUS));

      return;
    }

    dispatch(fsmSlice.actions.patchTransitions(fsmCfg));
  }, [partnerCountryCode, dispatch]);

  const state = useSelector(fsmSelectors.getValue);
  const handleSwitchScreen = useCallback(
    (event: TFsmEvent, newContext?: Record<string, string>) =>
      dispatch(fsmAsyncActions.sendEvent({ event, newContext })).unwrap(),
    [dispatch],
  );

  return (
    <WizardView
      partnerFirstName={partnerFirstName}
      partnerCountryCode={partnerCountryCode}
      onSwitchScreen={handleSwitchScreen}
      internalPath={state}
    />
  );
};

export { WizardManager };
